/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionNavigateToPageLink';

const ActionNavigateToPageLinkManager: ActionManagerFn<
  'ActionNavigateToPageLink'
> = (platform: Platform): ActionManager<'ActionNavigateToPageLink'> => {
  return {
    ...baseActionManager(platform, 'ActionNavigateToPageLink'),
    defaultValue,
    props: {
      ...baseActionProps,
      url: {
        label: 'Page Link',
        type: 'expression',
        returnType: async () => 'string',
        required: true,
      },
    },
    name: 'Navigate To Page Link',
    getDisplayText: (item) => item.props.url.value,
    icon: { iconName: 'external-link' },
    description: `**Overview**<br/>
The Navigate To Page Link action is a flexible routing component designed to transition the user to a specific destination using a URL string. Unlike "Go To Page" which relies on structured App and Page ID selections, this action mimics standard web hyperlinking, allowing developers to define the destination using a relative path or full URL pattern.<br/><br/>
This component is particularly useful for scenarios requiring dynamic route construction or when navigating to deep links that include query parameters directly in the string. It serves as a direct way to manipulate the browser's location, offering a more "web-native" approach to navigation within the platform.

**Key Features**<br/>
* **URL-Based Routing:** Accepts a string representing the target path (e.g., /myapp/mypage), giving developers full control over the link structure.
* **Parameter Support:** Explicitly supports appending query parameters directly to the URL string (e.g., ?param1=abc&param2=123), which can be parsed by the destination page.
* **Dynamic Resolution:** The url property is an expression, meaning the link can be constructed at runtime using variables (e.g., '/orders/' + #{selectedOrderId}).
* **Visual Context:** The editor displays the configured URL value directly in the action list (getDisplayText), providing immediate clarity on the destination.

**Properties**<br/>
* **Page Link (url) (Required):** An expression property returning a string.
  * *Function:* Defines the destination URL.
  * *Format:* Typically relative paths (e.g., /app/page) for internal navigation.

**Use Cases**<br/>
* **Dynamic Redirects:** Constructing a complex URL based on multiple form inputs to filter a search results page (e.g., /search?q=term&category=books).
* **Deep Linking:** Storing a specific page URL in a database (e.g., for a "Favorite Pages" list) and using this action to navigate to it when clicked.
* **Legacy Integration:** Mimicking existing web application routing structures during a migration process.`,
  };
};

export default ActionNavigateToPageLinkManager;
